import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';

export const Route = createFileRoute('/trade/')({
  component: TradePage,
});

function TradePage() {
  const toc = [
    { id: 'trade-overview', label: 'Overview' },
    { id: 'trade-balance', label: 'Trade Balance' },
    { id: 'tax-discipline', label: 'Tax Discipline' },
    { id: 'special-transfers', label: 'Special Transfers' },
    { id: 'aid-requests', label: 'Aid Requests' },
    { id: 'aid-workflows', label: 'Aid Workflows' },
  ];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="trade-overview" title="Trade & Aid">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img src="/images/nav/trade@3x.png" alt="Trade" className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25" />
          <div className="space-y-3">
            <p>
              Trade is a kingdom stabilization tool. Use aid to accelerate role readiness, recover chained
              provinces, and smooth war transitions without collapsing your own efficiency.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>War recovery</Badge>
              <Badge>Kingdom support</Badge>
              <Badge>Tax-aware logistics</Badge>
            </div>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="trade-balance" title="Trade Balance Basics">
        <p>
          Incoming and outgoing aid values influence your effective trade position. Keep your aid plan visible at
          kingdom level so support does not silently raise future aid friction for key provinces.
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ['Gold', 'High flexibility for emergency transfers'],
            ['Food', 'Stability during hostile pressure windows'],
            ['Runes', 'Magic uptime for key casting provinces'],
            ['Soldiers', 'Fast rebuild support after chain phases'],
            ['Servants', 'Account-level gem value that can be handed to kingdom mates'],
          ].map(([name, note]) => (
            <Card key={name} className="p-4">
              <h3 className="font-cinzel text-lg text-[#f0f0f0]">{name}</h3>
              <p className="text-sm text-[#b0b0b0]">{note}</p>
            </Card>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="tax-discipline" title="Tax Discipline">
        <ul className="list-disc space-y-1 pl-5">
          <li>Prefer planned aid cycles over repeated panic sends.</li>
          <li>Use role-based support queues (chain target, rune battery, econ anchor).</li>
          <li>Review tax exposure before major rebuild phases.</li>
          <li>Coordinate with diplomacy state because sustained incoming pressure changes needs quickly.</li>
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="special-transfers" title="Special Transfers">
        <p>
          Not every kingdom handoff is just gold and food. Servants can be aided to another player in your kingdom,
          which makes them the cleanest way to move long-term gem buying power to the province that will actually use it.
        </p>
        <p>
          That matters most when you are coordinating market purchases, setting up an auction push, or supporting a
          sitter who needs account-backed permanent gems to complete a bid plan.
        </p>
      </WikiAnchorSection>

      <WikiAnchorSection id="aid-requests" title="Aid Requests">
        <p>
          A province can ask the kingdom for one resource instead of waiting for someone to notice. Open the Aid page
          and choose gold, food, runes, soldiers, land, peasants or horses, then set the amount.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>One open request per resource, and at most three open requests per province.</li>
          <li>A kingdom holds at most 200 requests; each request can ask for up to one trillion units.</li>
          <li>Any kingdommate can contribute, and the request closes when its remaining amount reaches zero.</li>
          <li>The requester or the Monarch can cancel an open request.</li>
          <li>A request is only a request: the normal aid rules and limits still apply to the shipment that fills it.</li>
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="aid-workflows" title="Aid Workflows and Related Pages">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { to: '/kingdom-relations', label: 'Kingdom Relations' },
            { to: '/communication', label: 'Communication' },
            { to: '/economics', label: 'Economics' },
            { to: '/wiki/politics', label: 'Politics Hub' },
            { to: '/wiki/province-management', label: 'Province Management Hub' },
            { to: '/market', label: 'The Market' },
            { to: '/auction', label: 'Auction' },
            { to: '/gems', label: 'Servants & Gems' },
          ].map(item => (
            <Link key={item.to} to={item.to}>
              <Card className="p-4 transition-colors hover:border-[#d4af37]/40">
                <h3 className="font-cinzel text-lg text-[#f0f0f0]">{item.label}</h3>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
